import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories, mainSiteUrl, articleList } from '@/mocks/categories';

const categoryIcons: Record<number, string> = {
  1: 'ri-footprint-line',
  2: 'ri-t-shirt-line',
  3: 'ri-shirt-line',
  4: 'ri-archive-line',
  5: 'ri-layout-bottom-line',
  6: 'ri-red-packet-line',
  7: 'ri-stack-line',
  8: 'ri-user-3-line',
  9: 'ri-trophy-line',
  10: 'ri-glasses-line',
  11: 'ri-briefcase-line',
  12: 'ri-link',
  13: 'ri-drop-line',
  14: 'ri-more-line',
};

export default function Navbar() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileCatOpen, setIsMobileCatOpen] = useState(false);
  const [isMobileArticleOpen, setIsMobileArticleOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-background-50/95 backdrop-blur-md border-b border-background-200/60">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-[72px]">
          <Link to="/" className="flex items-center gap-2 text-foreground-950 hover:opacity-80 transition-opacity cursor-pointer whitespace-nowrap">
            <div className="w-8 h-8 md:w-9 md:h-9 bg-primary-500 rounded-lg flex items-center justify-center">
              <span className="text-background-50 font-bold text-sm">K</span>
            </div>
            <span className="font-semibold text-base md:text-lg tracking-tight">
              Kakobuy<span className="text-primary-500">Polska</span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            <Link to="/" className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-home-4-line text-base"></i>
              <span>Strona główna</span>
            </Link>

            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-menu-line text-base"></i>
                <span>Kategorie</span>
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {isDropdownOpen && (
                <div className="absolute top-full mt-2 left-0 w-64 bg-background-50 border border-background-200/70 rounded-xl shadow-lg overflow-hidden z-50 animate-[fadeIn_0.15s_ease-out]">
                  <div className="p-2 max-h-[420px] overflow-y-auto">
                    {categories.map((cat) => (
                      <a
                        key={cat.id}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
                        onClick={() => setIsDropdownOpen(false)}
                      >
                        <div className="w-7 h-7 rounded-md bg-background-100 flex items-center justify-center flex-shrink-0">
                          <i className={`${categoryIcons[cat.id] || 'ri-shopping-bag-line'} text-xs text-primary-500`}></i>
                        </div>
                        <span>{cat.name}</span>
                        <i className="ri-arrow-right-up-line text-foreground-400 text-xs ml-auto"></i>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link to="/jak-korzystac-z-kakobuy-spreadsheet" className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-book-open-line text-base"></i>
              <span>Poradnik zakupowy</span>
            </Link>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              Spreadsheet
            </a>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="ml-2 px-5 py-2 text-sm font-semibold bg-primary-500 text-background-50 dark:text-foreground-950 rounded-lg hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Kup Teraz
            </a>
          </nav>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-700 hover:text-foreground-950 cursor-pointer"
            aria-label="Menu"
          >
            <i className={`text-xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/60 animate-[fadeIn_0.15s_ease-out] overflow-y-auto max-h-[80vh]">
          <div className="px-4 py-3 space-y-1">
            <Link to="/" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-colors cursor-pointer">
              <div className="w-7 h-7 rounded-md bg-background-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-home-4-line text-xs text-primary-500"></i>
              </div>
              Strona główna
            </Link>

            <Link to="/jak-korzystac-z-kakobuy-spreadsheet" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-colors cursor-pointer">
              <div className="w-7 h-7 rounded-md bg-background-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-book-open-line text-xs text-primary-500"></i>
              </div>
              Poradnik zakupowy
            </Link>

            <div className="border-t border-background-200/60 my-2"></div>

            <button onClick={() => setIsMobileCatOpen(!isMobileCatOpen)} className="w-full flex items-center justify-between px-3 py-2.5 text-xs font-semibold text-foreground-500 uppercase tracking-wider cursor-pointer">
              <span>Kategorie produktów</span>
              <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${isMobileCatOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {isMobileCatOpen && (
              <div className="space-y-1 pl-1">
                {categories.map((cat) => (
                  <a
                    key={cat.id}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <div className="w-7 h-7 rounded-md bg-background-100 flex items-center justify-center flex-shrink-0">
                      <i className={`${categoryIcons[cat.id] || 'ri-shopping-bag-line'} text-xs text-primary-500`}></i>
                    </div>
                    <span>{cat.name}</span>
                    <i className="ri-arrow-right-up-line text-foreground-400 text-xs ml-auto"></i>
                  </a>
                ))}
              </div>
            )}

            <button onClick={() => setIsMobileArticleOpen(!isMobileArticleOpen)} className="w-full flex items-center justify-between px-3 py-2.5 text-xs font-semibold text-foreground-500 uppercase tracking-wider cursor-pointer mt-1">
              <span>Artykuły SEO</span>
              <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${isMobileArticleOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {isMobileArticleOpen && (
              <div className="space-y-1 pl-1">
                <Link to="/czym-jest-kakobuy-spreadsheet" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-colors cursor-pointer">
                  <div className="w-7 h-7 rounded-md bg-background-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-question-line text-xs text-primary-500"></i>
                  </div>
                  Czym jest Kakobuy Spreadsheet?
                </Link>
                {articleList.map((article) => (
                  <Link key={article.slug} to={`/${article.slug}`} onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-colors cursor-pointer">
                    <div className="w-7 h-7 rounded-md bg-background-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-article-line text-xs text-primary-500"></i>
                    </div>
                    <span className="truncate">{article.title.split('—')[0].trim()}</span>
                  </Link>
                ))}
              </div>
            )}

            <div className="border-t border-background-200/60 my-2"></div>

            <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="flex items-center justify-center px-3 py-2.5 text-sm font-semibold bg-primary-500 text-background-50 rounded-lg hover:bg-primary-600 transition-colors cursor-pointer" onClick={() => setIsMobileMenuOpen(false)}>
              Kup Teraz
            </a>
          </div>
        </div>
      )}
    </header>
  );
}